import { Link } from 'react-router-dom';
import { ArrowUpRight, ArrowRight } from 'lucide-react';
import PageShell from '@/components/PageShell';
import SectionHeader from '@/components/SectionHeader';
import PlatformCard from '@/components/PlatformCard';
import StatTicker from '@/components/StatTicker';
import VisionValueChain from '@/components/VisionValueChain';
import { PLATFORMS, SOLUTIONS, VENTURES } from '@/lib/platforms';
import { HERO } from '@/constants/testIds';

const heroImg =
  'https://images.unsplash.com/photo-1750969185331-e03829f72c7d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhdGElMjBtYXB8ZW58MHx8fHwxNzgxNzkyMTgyfDA&ixlib=rb-4.1.0&q=85';

const Home = () => {
  return (
    <PageShell>
      {/* HERO */}
      <section
        data-testid={HERO.root}
        className="relative overflow-hidden border-b border-white/10"
      >
        <div className="absolute inset-0">
          <img
            src={heroImg}
            alt="abstract data map"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/70 to-ink-900" />
          <div className="absolute inset-0 grid-lines opacity-60" />
        </div>

        <div className="relative px-6 md:px-12 lg:px-16 pt-28 pb-32 md:pt-36 md:pb-40">
          <div className="max-w-5xl">
            <div className="overline text-amber mb-6 fade-up">
              <span className="inline-flex items-center gap-2">
                <span className="h-px w-8 bg-amber inline-block" />
                themobilitygroup.co · Automotive · Mobility · Data · Capital
              </span>
            </div>
            <h1
              data-testid={HERO.heading}
              className="font-display text-[40px] sm:text-6xl lg:text-7xl xl:text-[88px] leading-[0.95] text-white text-balance fade-up"
              style={{ animationDelay: '60ms' }}
            >
              The technology, consulting &amp; investment group <br className="hidden md:block" />
              for the&nbsp;
              <span className="text-amber">automotive and mobility</span>{' '}
              ecosystem.
            </h1>
            <p
              className="mt-8 max-w-2xl text-zinc-300 text-base md:text-lg leading-relaxed fade-up"
              style={{ animationDelay: '140ms' }}
            >
              We build with founders and partners who actually run automotive
              technology businesses — combining operating depth, consulting
              rigour, and patient capital across the mobility value chain.
            </p>

            <div
              className="mt-10 flex flex-wrap items-center gap-4 fade-up"
              style={{ animationDelay: '220ms' }}
            >
              <Link
                to="/platforms"
                data-testid={HERO.primaryCta}
                className="group inline-flex items-center gap-2 border border-amber bg-amber px-6 py-3.5 text-xs font-mono uppercase tracking-[0.18em] text-black transition-colors hover:bg-white hover:border-white"
              >
                Explore Platforms
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <Link
                to="/contact"
                data-testid={HERO.secondaryCta}
                className="group inline-flex items-center gap-2 border border-white/25 px-6 py-3.5 text-xs font-mono uppercase tracking-[0.18em] text-white transition-colors hover:border-white hover:bg-white/5"
              >
                Partner With Us
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
            </div>

            <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/5 max-w-3xl border border-white/10 fade-up" style={{ animationDelay: '300ms' }}>
              {[
                ['397M+', 'Registered vehicles'],
                ['29.7M', 'FY26 retail sales'],
                ['28K+', 'Dealer outlets'],
                ['80+', 'OEMs'],
              ].map(([v, l]) => (
                <div key={l} className="bg-ink-900 px-5 py-4">
                  <div className="font-display text-2xl text-cyan">{v}</div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-500 mt-1">
                    {l}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Marquee */}
        <div className="relative border-t border-white/10 bg-black/40 overflow-hidden">
          <div className="flex marquee whitespace-nowrap py-3">
            {[...Array(2)].map((_, i) => (
              <div
                key={i}
                className="flex items-center gap-12 px-6 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500"
              >
              <span>MotoInsights.in</span><span>·</span>
                <span>DLIP</span><span>·</span>
                <span>Oto.bot</span><span>·</span>
                <span>VehicleLedger.in</span><span>·</span>
                <span>CarBikeClaims.com</span><span>·</span>
                <span>BayWise.ai</span><span>·</span>
                <span>AutoData.Exchange</span><span>·</span>
                <span>TrucksBuses.com</span><span>·</span>
                <span>Autorox.ai</span><span>·</span>
                <span>Autozilla.co</span><span>·</span>
                <span className="text-amber">themobilitygroup.co</span><span>·</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT WE DO */}
      <section className="px-6 md:px-12 lg:px-16 py-20 md:py-28 bg-ink-900">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-14">
          <div className="lg:col-span-7">
            <SectionHeader
              kicker="What we do"
              title="Three things, done with sector depth."
              intro="We don't dabble across sectors. The Mobility Group is built around automotive and mobility — and that focus shapes every platform, every solution, and every investment."
            />
          </div>
          <div className="lg:col-span-5 lg:pl-10 border-l border-white/10">
            <p className="text-zinc-400 leading-relaxed">
              Across India's 1,500+ RTOs, ~400 million vehicles, and a fast-changing
              EV and commercial vehicle landscape — we operate the data, the
              workflows, and the marketplaces that the industry runs on.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/5 border border-white/10">
          {[
            {
              n: '01',
              title: 'Operate platforms',
              desc: 'We own and run platforms that serve OEMs, dealerships, insurers, lenders, and fleets at national scale.',
            },
            {
              n: '02',
              title: 'Build solutions',
              desc: 'Custom data, analytics, and software for OEMs and dealer networks — built on top of live operating systems, not slideware.',
            },
            {
              n: '03',
              title: 'Invest in ventures',
              desc: 'We back automotive technology ventures we believe in — with capital, distribution, and operating muscle.',
            },
          ].map((b) => (
            <div key={b.n} className="bg-ink-800 p-8 md:p-10">
              <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-amber mb-6">
                / {b.n}
              </div>
              <h3 className="font-display text-2xl mb-3">{b.title}</h3>
              <p className="text-zinc-400 leading-relaxed text-sm">{b.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <StatTicker />

      {/* ECOSYSTEM TEASER BAND → /landscape */}
      <section className="border-b border-white/10 bg-black">
        <Link
          to="/landscape"
          className="group block px-6 md:px-12 lg:px-16 py-6 md:py-7 hover:bg-ink-900 transition-colors"
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-400">
              <span className="text-amber inline-flex items-center gap-2">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-amber" />
                </span>
                Beyond new vehicles
              </span>
              <span>Used cars · ~$37B</span>
              <span className="text-zinc-700">/</span>
              <span>Aftermarket · ~$13B</span>
              <span className="text-zinc-700">/</span>
              <span>EV share · ~8.5% · FY26</span>
              <span className="text-zinc-700">/</span>
              <span>Internet-first ventures · 200+</span>
            </div>
            <div className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-white group-hover:text-amber transition-colors shrink-0">
              See the full India landscape
              <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </div>
          </div>
        </Link>
      </section>

      {/* PLATFORMS GRID */}
      <section className="px-6 md:px-12 lg:px-16 py-20 md:py-28">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <SectionHeader
            kicker="Platform ecosystem"
            title="A growing stack. One mobility ecosystem."
            intro="Each platform solves a specific problem in the automotive lifecycle — and together they form a connected operating system for the industry, spanning everything we have built and everything we have backed."
          />
          <Link
            to="/platforms"
            className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-amber hover:text-white transition-colors"
          >
            All platforms <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {PLATFORMS.map((p, i) => (
            <PlatformCard key={p.slug} platform={p} index={i} />
          ))}
        </div>
      </section>

      <VisionValueChain />

      {/* SOLUTIONS */}
      <section className="px-6 md:px-12 lg:px-16 py-20 md:py-28 bg-black border-y border-white/10">
        <SectionHeader
          kicker="Solutions"
          title="Built for the entire automotive value chain."
          intro="From the OEM boardroom to the dealership floor to the insurer's underwriting desk — we ship solutions where the work actually happens."
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/5 border border-white/10">
          {SOLUTIONS.map((s) => (
            <div key={s.audience} className="bg-ink-900 p-8 md:p-10">
              <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-cyan mb-4">
                For {s.audience}
              </div>
              <h3 className="font-display text-2xl md:text-3xl text-white mb-5">
                {s.title}
              </h3>
              <ul className="space-y-2.5">
                {s.points.map((pt) => (
                  <li key={pt} className="flex items-start gap-3 text-zinc-300 text-sm">
                    <span className="text-amber font-mono mt-1">→</span>
                    <span>{pt}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="mt-10">
          <Link
            to="/solutions"
            className="inline-flex items-center gap-2 border border-white/25 px-6 py-3 text-xs font-mono uppercase tracking-[0.18em] text-white hover:border-white hover:bg-white/5 transition-colors"
          >
            See all solutions <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </section>

      {/* WHY PARTNER WITH US */}
      <section className="px-6 md:px-12 lg:px-16 py-20 md:py-28 bg-ink-900 border-b border-white/10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-7">
            <SectionHeader
              kicker="Why OEMs, dealers & insurers partner with us"
              title="We come in as partners — not vendors."
              intro="Most providers either sell software or write reports. We do neither in isolation. We bring operators, technology, AI, data, and capital together — and stay on the hook for outcomes."
            />
          </div>
          <div className="lg:col-span-5 lg:pl-10 border-l border-white/10">
            <p className="text-zinc-400 leading-relaxed">
              We've already built and scaled the platforms most of the
              industry is trying to build. That means faster time-to-value,
              fewer experiments on your dime, and a partner who shows up with
              the playbook already written — and is willing to put skin in the
              game.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5 border border-white/10">
          {[
            {
              n: '01',
              t: 'Operators, not consultants',
              d: 'Our team has run OEM functions, dealer networks, and category-defining auto-tech businesses. We give advice that has been stress-tested in real P&Ls.',
            },
            {
              n: '02',
              t: 'Technology & platforms, ready to plug in',
              d: 'DLIP, Oto.bot, MotoInsights.in, VehicleLedger.in, CarBikeClaims.com, BayWise.ai, AutoData.Exchange — production-grade platforms you can build on, not greenfield projects.',
            },
            {
              n: '03',
              t: 'AI & data analytics, sector-trained',
              d: 'Models trained on ~400M vehicle records, 1,500+ RTOs, OEM dispatch data, dealer SLAs and claims feeds — not generic LLMs guessing at automotive.',
            },
            {
              n: '04',
              t: 'Agility of a startup, depth of a strategy firm',
              d: 'Small senior teams, weekly outcomes, decisions in days. The rigour of consulting without the slowness of consulting.',
            },
            {
              n: '05',
              t: 'Skin in the game',
              d: 'We work on outcome-linked, revenue-share, or equity structures wherever it makes sense. Our incentives sit alongside yours.',
            },
            {
              n: '06',
              t: 'India-scale + global lens',
              d: 'Built across the most diverse vehicle market on earth — and selectively exported to similar emerging and developed markets.',
            },
          ].map((b) => (
            <div key={b.n} className="bg-ink-800 p-8 hover-trace hover:bg-ink-700 transition-colors">
              <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-amber mb-5">
                / {b.n}
              </div>
              <h3 className="font-display text-xl text-white mb-3">{b.t}</h3>
              <p className="text-zinc-400 text-sm leading-relaxed">{b.d}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-4">
          <Link
            to="/contact"
            className="group inline-flex items-center gap-2 border border-amber bg-amber px-6 py-3 text-xs font-mono uppercase tracking-[0.18em] text-black transition-colors hover:bg-white hover:border-white"
          >
            Explore a partnership
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
          <Link
            to="/about"
            className="inline-flex items-center gap-2 border border-white/25 px-6 py-3 text-xs font-mono uppercase tracking-[0.18em] text-white hover:border-white hover:bg-white/5 transition-colors"
          >
            About the group <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </section>

      {/* INVESTMENTS preview */}
      <section className="px-6 md:px-12 lg:px-16 py-20 md:py-28">
        <SectionHeader
          kicker="Investment arm"
          title="We back ventures we'd build ourselves."
          intro="From commercial vehicle marketplaces to data exchanges and EV infrastructure — we invest where our operating know-how creates an unfair advantage."
        />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {VENTURES.map((v) => (
            <div
              key={v.name}
              className="bg-ink-800 border border-white/10 p-6 flex flex-col gap-4 hover:border-amber/40 transition-colors"
            >
              <div className="flex items-center justify-between">
                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-cyan border border-cyan/30 px-2 py-1">
                  {v.sector}
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                  {v.stake}
                </span>
              </div>
              <div>
                <h3 className="font-display text-xl text-white">{v.name}</h3>
                <p className="text-zinc-400 text-sm leading-relaxed mt-2">{v.summary}</p>
              </div>
              {v.url && (
                <a
                  href={v.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-auto inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-amber hover:text-white transition-colors"
                >
                  Visit ↗
                </a>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="relative px-6 md:px-12 lg:px-16 py-24 md:py-32 bg-ink-900 border-t border-white/10 overflow-hidden">
        <div className="absolute inset-0 radial-glow-amber opacity-60" />
        <div className="relative max-w-4xl">
          <div className="overline text-amber mb-6">
            <span className="inline-flex items-center gap-2">
              <span className="h-px w-8 bg-amber inline-block" />
              Build with The Mobility Group
            </span>
          </div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-balance leading-[1.02]">
            If it moves on wheels and runs on data — we probably already understand it.
          </h2>
          <p className="mt-6 text-zinc-300 text-lg max-w-2xl">
            Whether you're an OEM, a dealer network, an insurer, a financier,
            or a founder — let's see if there's something worth building together.
          </p>
          <div className="mt-10">
            <Link
              to="/contact"
              className="group inline-flex items-center gap-2 border border-amber bg-amber px-8 py-4 text-xs font-mono uppercase tracking-[0.18em] text-black transition-colors hover:bg-white hover:border-white"
            >
              Start a conversation
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
        </div>
      </section>
    </PageShell>
  );
};

export default Home;
